/**
 * Session orchestration for the guest-first flow (wiki/AUTH-DESIGN.md).
 *
 * The two load-bearing invariants live here and are dependency-injected so
 * they can be unit-tested without touching AWS:
 *   1. Guest bootstrap caches the IdentityId (uid) once.
 *   2. The signup link REUSES that cached IdentityId — never re-mints it — so
 *      the uid is identical before and after signup.
 */
import { authConfig, userPoolLoginKey, type AuthConfig } from './config';
import * as cognito from './cognito';
import * as hostedUi from './hostedUi';
import * as storage from './storage';
import type { AuthTokens, AwsCredentials, PersistedSession } from './types';

/** The Cognito operations the flow needs (real impl in cognito.ts). */
export interface CognitoAdapter {
  getGuestIdentityId(): Promise<string>;
  getCredentialsForIdentity(
    identityId: string,
    logins?: Record<string, string>,
  ): Promise<AwsCredentials>;
  signUp(email: string, password: string): Promise<void>;
  confirmSignUp(email: string, code: string): Promise<void>;
  srpSignIn(email: string, password: string): Promise<AuthTokens>;
  /** Exchange a Hosted UI authorization code for User Pool tokens (PKCE). */
  exchangeGoogleCode(
    code: string,
    codeVerifier: string,
    redirectUri: string,
  ): Promise<AuthTokens>;
  /**
   * GetId with a login → the IdentityId Cognito already has for that login.
   * Fallback path when the login is already linked to a different identity.
   */
  resolveIdentityForLogin(idToken: string): Promise<string>;
}

/** Device-local persistence the flow needs (real impl in storage.ts). */
export interface SessionStorage {
  readIdentityId(): string | null;
  persistIdentityId(identityId: string): void;
  readSession(): PersistedSession | null;
  persistSession(session: PersistedSession): void;
  clearSession(): void;
}

export interface AuthDeps {
  cognito: CognitoAdapter;
  storage: SessionStorage;
  /** Identity Pool logins key for our User Pool. */
  loginKey: string;
}

/** Real dependencies wiring the SDK + localStorage. */
export function realAuthDeps(config: AuthConfig = authConfig): AuthDeps {
  return {
    cognito: {
      getGuestIdentityId: () => cognito.getGuestIdentityId(config),
      getCredentialsForIdentity: (identityId, logins) =>
        cognito.getCredentialsForIdentity(identityId, logins, config),
      signUp: (email, password) => cognito.signUp(email, password, config),
      confirmSignUp: (email, code) => cognito.confirmSignUp(email, code, config),
      srpSignIn: (email, password) => cognito.srpSignIn(email, password, config),
      exchangeGoogleCode: (code, codeVerifier, redirectUri) =>
        hostedUi.exchangeCodeForTokens(code, codeVerifier, redirectUri, config),
      resolveIdentityForLogin: (idToken) => cognito.resolveIdentityForLogin(idToken, config),
    },
    storage: {
      readIdentityId: storage.readIdentityId,
      persistIdentityId: storage.persistIdentityId,
      readSession: storage.readSession,
      persistSession: storage.persistSession,
      clearSession: storage.clearSession,
    },
    loginKey: userPoolLoginKey(config),
  };
}

/**
 * Silent guest bootstrap: return the cached uid if present, else GetId a fresh
 * IdentityId and cache it. Idempotent — a second call returns the cached uid
 * without another GetId.
 */
export async function bootstrapGuest(deps: AuthDeps): Promise<string> {
  const cached = deps.storage.readIdentityId();
  if (cached) return cached;
  const identityId = await deps.cognito.getGuestIdentityId();
  deps.storage.persistIdentityId(identityId);
  return identityId;
}

export interface LinkedIdentity {
  identityId: string;
  credentials: AwsCredentials;
  /**
   * Set only when the login was already linked to a DIFFERENT identity than
   * the cached guest uid (a returning Google user on a new device, or
   * cleared local storage) — the resolved identity replaced the guest one.
   * The caller uses this to trigger an account-merge on the next authed
   * `session/complete` call. Undefined on the common same-uid path.
   */
  previousGuestUid?: string;
}

/**
 * True for the Cognito exception `GetCredentialsForIdentity` throws when a
 * login is already linked to an identity other than the one requested.
 * `ResourceConflictException` is documented as one of that API's own error
 * responses (not inherited from GetId): see the "Errors" list on
 * https://docs.aws.amazon.com/cognitoidentity/latest/APIReference/API_GetCredentialsForIdentity.html
 */
function isIdentityConflict(err: unknown): boolean {
  return err instanceof Error && err.name === 'ResourceConflictException';
}

/**
 * Link a freshly signed-in User Pool account onto the guest's EXISTING
 * IdentityId. Reuses the cached uid and passes it to GetCredentialsForIdentity
 * with the id-token login — the pool attaches the login to that same identity,
 * so the returned identityId equals the cached uid on the common path. Never
 * calls GetId directly (which could mint a new id) for that path.
 *
 * If the login is already linked to a different identity (returning
 * Google/email user, new device or cleared storage), GetCredentialsForIdentity
 * rejects with a conflict; falls back to resolving that login's own identity
 * and re-requesting credentials for it, reporting the guest uid as
 * `previousGuestUid` so the caller can merge the two.
 */
export async function linkAccount(
  deps: AuthDeps,
  idToken: string,
): Promise<LinkedIdentity> {
  const cachedId = deps.storage.readIdentityId();
  if (!cachedId) {
    throw new Error('Cannot link account before guest bootstrap: no cached uid');
  }
  try {
    const credentials = await deps.cognito.getCredentialsForIdentity(cachedId, {
      [deps.loginKey]: idToken,
    });
    return { identityId: cachedId, credentials };
  } catch (err) {
    if (!isIdentityConflict(err)) throw err;
    const resolvedId = await deps.cognito.resolveIdentityForLogin(idToken);
    const credentials = await deps.cognito.getCredentialsForIdentity(resolvedId, {
      [deps.loginKey]: idToken,
    });
    return {
      identityId: resolvedId,
      credentials,
      previousGuestUid: resolvedId !== cachedId ? cachedId : undefined,
    };
  }
}

/**
 * Confirm a signup and sign in. The confirmation code path is for humans; if
 * the account is already confirmed (admin-confirm in tests, a prior confirm,
 * or a double submit) ConfirmSignUp throws but sign-in still succeeds — treat
 * that as confirmed. A genuinely wrong/expired code leaves the account
 * UNCONFIRMED, so sign-in fails and the original confirm error is surfaced.
 */
export async function confirmAndSignIn(
  deps: AuthDeps,
  email: string,
  code: string,
  password: string,
): Promise<AuthTokens> {
  try {
    await deps.cognito.confirmSignUp(email, code);
  } catch (confirmError) {
    try {
      return await deps.cognito.srpSignIn(email, password);
    } catch {
      throw confirmError;
    }
  }
  return deps.cognito.srpSignIn(email, password);
}
